import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { useSearchInventoryTags } from '../../../hooks/inventory/useSearchInventoryTags';
import { useSearchInventoryBins } from '../../../hooks/inventory/useSearchInventoryBins';
import { useSearchSuppliers } from '../../../hooks/useSearchSuppliers';
import type { InventoryBin, InventoryPartType, InventoryTag } from '../../../types/inventory';
import { INVENTORY_PART_TYPE_OPTIONS } from '../../../utils/inventoryConstants';

export interface ReportingInventoryFilterSnapshot {
    selectedLocations: string[] | 'all';
    selectedPartTypes: InventoryPartType[];
    selectedTagIds: string[];
    selectedBinIds: string[];
    selectedSupplierIds: string[];
    tagLabelById: Record<string, string>;
    binLabelById: Record<string, string>;
    supplierLabelById: Record<string, string>;
}

export interface ReportingInventoryApiFilters {
    locationIds?: string[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
}

export function useReportingInventoryFilters({
    locationId,
    isActive = true,
    searchQuery = '',
}: {
    locationId?: string;
    isActive?: boolean;
    /** Debounced externally or passed raw; hook debounces internally when used as searchQuery. */
    searchQuery?: string;
}) {
    const [selectedLocations, setSelectedLocations] = useState<string[] | 'all'>(() =>
        locationId ? [locationId] : [],
    );
    const [selectedPartTypes, setSelectedPartTypes] = useState<InventoryPartType[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    const [selectedBinIds, setSelectedBinIds] = useState<string[]>([]);
    const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
    const [tagLabelById, setTagLabelById] = useState<Record<string, string>>({});
    const [binLabelById, setBinLabelById] = useState<Record<string, string>>({});
    const [supplierLabelById, setSupplierLabelById] = useState<Record<string, string>>({});

    const [tagSearch, setTagSearch] = useState('');
    const [debouncedTagSearch, setDebouncedTagSearch] = useState('');
    const [binSearch, setBinSearch] = useState('');
    const [debouncedBinSearch, setDebouncedBinSearch] = useState('');
    const [supplierSearch, setSupplierSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (locationId) setSelectedLocations([locationId]);
    }, [locationId]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 250);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedTagSearch(tagSearch.trim()), 250);
        return () => clearTimeout(t);
    }, [tagSearch]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBinSearch(binSearch.trim()), 250);
        return () => clearTimeout(t);
    }, [binSearch]);

    const locationIdsForApi = useMemo(() => {
        if (locationId) return [locationId];
        if (selectedLocations === 'all' || selectedLocations.length === 0) return undefined;
        return selectedLocations;
    }, [locationId, selectedLocations]);

    const binLocationScope = useMemo((): string[] | 'all' => {
        if (locationId) return [locationId];
        if (selectedLocations === 'all' || selectedLocations.length === 0) return 'all';
        return selectedLocations;
    }, [locationId, selectedLocations]);

    const { data: tagsData, isFetching: isTagsLoading } = useSearchInventoryTags(
        debouncedTagSearch,
        30,
        isActive,
    );
    const { data: binsData, isFetching: isBinsLoading } = useSearchInventoryBins(
        binLocationScope,
        debouncedBinSearch,
        30,
        isActive,
    );
    const { data: suppliersData, isFetching: isSuppliersLoading } = useSearchSuppliers('', {
        listAll: true,
    });

    useEffect(() => {
        if (!tagsData?.tags) return;
        setTagLabelById((prev) => {
            const next = { ...prev };
            for (const t of tagsData.tags as InventoryTag[]) {
                next[t._id] = t.name;
            }
            return next;
        });
    }, [tagsData?.tags]);

    useEffect(() => {
        if (!binsData?.bins) return;
        setBinLabelById((prev) => {
            const next = { ...prev };
            for (const b of binsData.bins as InventoryBin[]) {
                next[b._id] = b.name;
            }
            return next;
        });
    }, [binsData?.bins]);

    useEffect(() => {
        if (!suppliersData?.suppliers) return;
        setSupplierLabelById((prev) => {
            const next = { ...prev };
            for (const s of suppliersData.suppliers) {
                next[s._id] = s.title;
            }
            return next;
        });
    }, [suppliersData?.suppliers]);

    const toggleId = (
        setIds: Dispatch<SetStateAction<string[]>>,
        setLabels: Dispatch<SetStateAction<Record<string, string>>>,
        value: string,
        label: string,
    ) => {
        setIds((ids) => {
            if (ids.includes(value)) return ids.filter((id) => id !== value);
            return [...ids, value];
        });
        setLabels((prev) => ({ ...prev, [value]: label }));
    };

    const partTypeOptions = INVENTORY_PART_TYPE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: opt.label,
    }));

    const tagOptions = useMemo(() => {
        const fromSearch = (tagsData?.tags ?? []).map((t: InventoryTag) => ({
            value: t._id,
            label: t.name,
        }));
        const selectedExtras = selectedTagIds
            .filter((id) => !fromSearch.some((o) => o.value === id))
            .map((id) => ({ value: id, label: tagLabelById[id] ?? id }));
        return [...selectedExtras, ...fromSearch];
    }, [tagsData?.tags, selectedTagIds, tagLabelById]);

    const binOptions = useMemo(() => {
        const fromSearch = (binsData?.bins ?? []).map((b: InventoryBin) => ({
            value: b._id,
            label: b.name,
        }));
        const selectedExtras = selectedBinIds
            .filter((id) => !fromSearch.some((o) => o.value === id))
            .map((id) => ({ value: id, label: binLabelById[id] ?? id }));
        return [...selectedExtras, ...fromSearch];
    }, [binsData?.bins, selectedBinIds, binLabelById]);

    const supplierOptions = useMemo(() => {
        const q = supplierSearch.trim().toLowerCase();
        const fromSearch = (suppliersData?.suppliers ?? [])
            .filter((s) => !q || s.title.toLowerCase().includes(q))
            .map((s) => ({
                value: s._id,
                label: s.title,
            }));
        const selectedExtras = selectedSupplierIds
            .filter((id) => !fromSearch.some((o) => o.value === id))
            .map((id) => ({ value: id, label: supplierLabelById[id] ?? id }));
        return [...selectedExtras, ...fromSearch];
    }, [suppliersData?.suppliers, selectedSupplierIds, supplierLabelById, supplierSearch]);

    const apiFilters: ReportingInventoryApiFilters = useMemo(
        () => ({
            locationIds: locationIdsForApi,
            tagIds: selectedTagIds.length ? selectedTagIds : undefined,
            binIds: selectedBinIds.length ? selectedBinIds : undefined,
            partTypes: selectedPartTypes.length ? selectedPartTypes : undefined,
            supplierIds: selectedSupplierIds.length ? selectedSupplierIds : undefined,
            search: debouncedSearch || undefined,
        }),
        [
            locationIdsForApi,
            selectedTagIds,
            selectedBinIds,
            selectedPartTypes,
            selectedSupplierIds,
            debouncedSearch,
        ],
    );

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (!locationId && selectedLocations !== 'all' && selectedLocations.length > 0) n += 1;
        if (selectedPartTypes.length > 0) n += 1;
        if (selectedTagIds.length > 0) n += 1;
        if (selectedBinIds.length > 0) n += 1;
        if (selectedSupplierIds.length > 0) n += 1;
        return n;
    }, [
        locationId,
        selectedLocations,
        selectedPartTypes,
        selectedTagIds,
        selectedBinIds,
        selectedSupplierIds,
    ]);

    const getSnapshot = useCallback((): ReportingInventoryFilterSnapshot => ({
        selectedLocations,
        selectedPartTypes,
        selectedTagIds,
        selectedBinIds,
        selectedSupplierIds,
        tagLabelById,
        binLabelById,
        supplierLabelById,
    }), [
        selectedLocations,
        selectedPartTypes,
        selectedTagIds,
        selectedBinIds,
        selectedSupplierIds,
        tagLabelById,
        binLabelById,
        supplierLabelById,
    ]);

    const restoreSnapshot = useCallback((snap: ReportingInventoryFilterSnapshot) => {
        setSelectedLocations(snap.selectedLocations);
        setSelectedPartTypes(snap.selectedPartTypes);
        setSelectedTagIds(snap.selectedTagIds);
        setSelectedBinIds(snap.selectedBinIds);
        setSelectedSupplierIds(snap.selectedSupplierIds);
        setTagLabelById(snap.tagLabelById);
        setBinLabelById(snap.binLabelById);
        setSupplierLabelById(snap.supplierLabelById);
    }, []);

    const clearAllFilters = useCallback(() => {
        setSelectedLocations(locationId ? [locationId] : []);
        setSelectedPartTypes([]);
        setSelectedTagIds([]);
        setSelectedBinIds([]);
        setSelectedSupplierIds([]);
    }, [locationId]);

    return {
        locationId,
        selectedLocations,
        setSelectedLocations,
        selectedPartTypes,
        setSelectedPartTypes,
        selectedTagIds,
        setSelectedTagIds,
        selectedBinIds,
        setSelectedBinIds,
        selectedSupplierIds,
        setSelectedSupplierIds,
        tagLabelById,
        setTagLabelById,
        binLabelById,
        setBinLabelById,
        supplierLabelById,
        setSupplierLabelById,
        tagSearch,
        setTagSearch,
        binSearch,
        setBinSearch,
        supplierSearch,
        setSupplierSearch,
        isTagsLoading,
        isBinsLoading,
        isSuppliersLoading,
        partTypeOptions,
        tagOptions,
        binOptions,
        supplierOptions,
        toggleId,
        apiFilters,
        locationIdsForApi,
        binLocationScope,
        debouncedSearch,
        activeFilterCount,
        getSnapshot,
        restoreSnapshot,
        clearAllFilters,
        tagsData,
        binsData,
        suppliersData,
    };
}

export type ReportingInventoryFiltersModel = ReturnType<typeof useReportingInventoryFilters>;
